/**
 * Precomputed navigation fields (Movement spec §1.3). Built once per map/match in
 * the same pass as the grid and handed to agents read-only — bots do not compute
 * world knowledge on the fly.
 *
 * - Dijkstra distance field: retreat is then one array lookup per step, not a search.
 * - Chokepoint map: where agents make a stand / hold offensively.
 * - Cover / openness: low-HP agents hug cover and avoid open ground.
 */

import { NavGrid, NavFlag, tileIndex, diagonalOpen } from './grid';

const DIRS8 = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]] as const;
const STEP = (dx: number, dy: number) => (dx !== 0 && dy !== 0 ? Math.SQRT2 : 1);

// A tiny binary min-heap keyed by cost (shared by the field builders).
class MinHeap {
  private c: number[] = [];
  private i: number[] = [];
  get size() { return this.c.length; }
  push(cost: number, idx: number) {
    this.c.push(cost); this.i.push(idx);
    let n = this.c.length - 1;
    while (n > 0) { const p = (n - 1) >> 1; if (this.c[p] <= this.c[n]) break; this.swap(p, n); n = p; }
  }
  pop(): { cost: number; idx: number } {
    const cost = this.c[0], idx = this.i[0], lc = this.c.pop()!, li = this.i.pop()!;
    if (this.c.length) { this.c[0] = lc; this.i[0] = li; this.down(0); }
    return { cost, idx };
  }
  private down(n: number) {
    for (;;) {
      const l = 2 * n + 1, r = l + 1; let s = n;
      if (l < this.c.length && this.c[l] < this.c[s]) s = l;
      if (r < this.c.length && this.c[r] < this.c[s]) s = r;
      if (s === n) break; this.swap(s, n); n = s;
    }
  }
  private swap(a: number, b: number) {
    [this.c[a], this.c[b]] = [this.c[b], this.c[a]];
    [this.i[a], this.i[b]] = [this.i[b], this.i[a]];
  }
}

/**
 * Multi-source Dijkstra distance field over the grid's movement cost. Cost to reach
 * the nearest source tile from every tile; Infinity if unreachable. Diagonals obey
 * the no-corner-cut rule so the field matches how agents actually walk.
 */
export function dijkstraField(grid: NavGrid, sources: number[]): Float64Array {
  const { w, h, walkable, cost } = grid;
  // Float64, NOT Float32: the heap holds full-precision costs, so storing the same
  // value rounded to Float32 makes the popped double compare as *greater* than the
  // stored distance, the node is wrongly skipped as stale, and the field stops
  // spreading. Doubles keep push and store bit-identical.
  const dist = new Float64Array(w * h).fill(Infinity);
  const heap = new MinHeap();
  for (const s of sources) { if (walkable[s]) { dist[s] = 0; heap.push(0, s); } }
  while (heap.size) {
    const { cost: c, idx: i } = heap.pop();
    if (c > dist[i]) continue;
    const x = i % w, y = (i - x) / w;
    for (const [dx, dy] of DIRS8) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const ni = tileIndex(nx, ny, w);
      if (!walkable[ni]) continue;
      if (!diagonalOpen(walkable, w, h, x, y, dx, dy)) continue;
      const nc = c + STEP(dx, dy) * cost[ni];
      if (nc < dist[ni]) { dist[ni] = nc; heap.push(nc, ni); }
    }
  }
  return dist;
}

/** Indices of every tile carrying a given flag (e.g. the safe space, the tent). */
export function tilesWithFlag(grid: NavGrid, flag: NavFlag): number[] {
  const out: number[] = [];
  for (let i = 0; i < grid.flags.length; i++) if (grid.flags[i] & flag) out.push(i);
  return out;
}

/**
 * Chokepoints (spec §1.3): narrow tiles agents funnel through. Practical detector —
 * every BRIDGE-flagged tile, plus any walkable tile that is 1-wide (walkable on
 * exactly one axis, walled on the other) with clearance ≤ 2. Sets the CHOKEPOINT flag
 * in-place and returns the list. (A full articulation-point pass is the stricter
 * definition; this heuristic catches bridges, mushroom gaps and cave mouths, which is
 * what the AI holds/retreats through, at a fraction of the cost.)
 */
export function markChokepoints(grid: NavGrid): number[] {
  const { w, h, walkable, clearance, flags } = grid;
  const out: number[] = [];
  const pass = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && walkable[tileIndex(x, y, w)] === 1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = tileIndex(x, y, w);
    if (!walkable[i]) continue;
    let choke = (flags[i] & NavFlag.BRIDGE) !== 0;
    if (!choke && clearance[i] <= 2) {
      const horiz = pass(x - 1, y) && pass(x + 1, y);
      const vert = pass(x, y - 1) && pass(x, y + 1);
      if (horiz && !vert && !pass(x, y - 1) && !pass(x, y + 1)) choke = true;
      else if (vert && !horiz && !pass(x - 1, y) && !pass(x + 1, y)) choke = true;
    }
    if (choke) { flags[i] |= NavFlag.CHOKEPOINT; out.push(i); }
  }
  return out;
}

/**
 * Cover map (spec §1.3): walkable tiles with ≥3 of their 8 neighbours blocked — a
 * blocker screens approach from those directions. Sets the COVER flag; returns the
 * count. Low-HP agents seek these.
 */
export function markCover(grid: NavGrid): number {
  const { w, h, walkable, flags } = grid;
  let count = 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = tileIndex(x, y, w);
    if (!walkable[i]) continue;
    let blocked = 0;
    for (const [dx, dy] of DIRS8) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h || !walkable[tileIndex(nx, ny, w)]) blocked++;
    }
    if (blocked >= 3) { flags[i] |= NavFlag.COVER; count++; }
  }
  return count;
}

/**
 * Openness (spec §1.3): the inverse of cover — how exposed a tile is, 0 (walled in)
 * to 1 (open field), from the fraction of walkable 8-neighbours. Low-HP agents avoid
 * high-openness tiles.
 */
export function opennessMap(grid: NavGrid): Float32Array {
  const { w, h, walkable } = grid;
  const open = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = tileIndex(x, y, w);
    if (!walkable[i]) { open[i] = 0; continue; }
    let free = 0;
    for (const [dx, dy] of DIRS8) {
      const nx = x + dx, ny = y + dy;
      if (nx >= 0 && ny >= 0 && nx < w && ny < h && walkable[tileIndex(nx, ny, w)]) free++;
    }
    open[i] = free / 8;
  }
  return open;
}
